<p-toast position="top-right"></p-toast>
<app-page-layout [pageTitle]="client()?.clientId || 'Client'" subtitle="Client details">

  <!-- State Container -->
  <app-state-container
    [state]="viewState()"
    loadingMessage="Loading client details..."
    loadingSubMessage="Please wait while we fetch the client information"
    emptyIcon="pi-id-card"
    emptyTitle="Client not found"
    [emptyMessage]="emptyStateMessage()"
    [showEmptyAction]="false"
    [showClearFilters]="false"
    [errorMessage]="errorMessage()"
    (retry)="retryLoad()"
  >
    @if (c) {
      <div class="flex flex-col lg:flex-row gap-10">

        <!-- ================= LEFT (VIEW) ================= -->
        <div class="w-full lg:w-1/3 space-y-6">

          <div class="space-y-4">

            <div>
              <p class="text-surface-500 dark:text-surface-400">Client Name</p>
              <p class="font-semibold text-surface-800 dark:text-surface-100">
                {{ c.clientName }}
              </p>
            </div>

            <div>
              <p class="text-surface-500 dark:text-surface-400">Client ID</p>
              <p class="font-medium text-surface-700 dark:text-surface-200">
                {{ c.id }}
              </p>
            </div>

            <div>
              <p class="text-surface-500 dark:text-surface-400">Created</p>
              <p class="text-surface-700 dark:text-surface-200">
                {{ c.createdDate | date:'dd MMM yyyy, hh:mm a' }}
              </p>
            </div>

            <!-- <div>
              <p class="text-surface-500 dark:text-surface-400">Description</p>
              <p class="text-surface-700 dark:text-surface-200">
                {{ c.description || '-' }}
              </p>
            </div> -->

          </div>
        </div>

        <!-- ================= RIGHT (EDIT) ================= -->
        @if (form) {
          <form
            [formGroup]="form"
            class="w-full lg:w-2/3 space-y-8"
          >
            <div formArrayName="scopes">
              <h3 class="text-base font-semibold text-surface-900 dark:text-surface-0 mb-1">
                Scopes
              </h3>

              <div class="space-y-2">
                @for (ctrl of scopes.controls; track ctrl; let i = $index) {
                  <div class="flex items-stretch gap-2 group">
                    <input
                      pInputText
                      [formControl]="ctrl"
                      class="w-full py-1! px-2!"
                    />
                    <button
                      pButton
                      icon="pi pi-trash"
                      severity="danger"
                      size="small"
                      type="button"
                      class="opacity-0 group-hover:opacity-100 transition-opacity"
                      (click)="deleteItem('scopes', i)">
                    </button>
                  </div>
                }
                <button
                  pButton
                  label="Add Scope"
                  icon="pi pi-plus"
                  outlined
                  size="small"
                  type="button"
                  (click)="addItem('scopes')">
                </button>
              </div>
            </div>

            <!-- REDIRECT URIs -->
            <div formArrayName="redirectUris">
              <h3 class="text-base font-semibold text-surface-900 dark:text-surface-0 mb-1">
                Redirect URIs
              </h3>
              <div class="space-y-2">
                @for (ctrl of redirectUris.controls; track ctrl; let i = $index) {
                  <div class="flex items-stretch gap-2 group">
                    <input
                      pInputText
                      [formControl]="ctrl"
                      class="w-full py-1! px-2!"
                    />
                    <button
                      pButton
                      icon="pi pi-trash"
                      severity="danger"
                      size="small"
                      type="button"
                      class="opacity-0 group-hover:opacity-100 transition-opacity"
                      (click)="deleteItem('redirectUris', i)">
                    </button>
                  </div>
                }
                <button
                  pButton
                  label="Add URI"
                  icon="pi pi-plus"
                  outlined
                  size="small"
                  type="button"
                  (click)="addItem('redirectUris')">
                </button>

              </div>
            </div>

            <!-- GRANT TYPES -->
            <div formArrayName="authorizationGrantTypes">
              <h3 class="text-base font-semibold text-surface-900 dark:text-surface-0 mb-2">
                Authorization Grant Types
              </h3>

              <div class="grid grid-cols-2 md:grid-cols-3 gap-3">
                @for (type of authorizationGrantTypeOptions(); track type) {
                  <div
                    class="flex items-center gap-2 rounded-lg px-3 py-2 cursor-pointer
                 hover:border-primary transition"
                  >
                    <p-checkbox
                      [binary]="true"
                      [ngModel]="isGrantTypeSelected(type)"
                      [ngModelOptions]="{ standalone: true }"
                      (onChange)="toggleGrantType(type)"
                    ></p-checkbox>

                    <span>{{ type }}</span>
                  </div>
                }
              </div>
            </div>

            <!-- POST LOGOUT REDIRECT URIs -->
            <div formArrayName="postLogoutRedirectUris">
              <h3 class="text-base font-semibold text-surface-900 dark:text-surface-0 mb-1">
                Post Logout Redirect URIs
              </h3>
              <div class="space-y-2">
                @for (ctrl of postLogoutRedirectUris.controls; track ctrl; let i = $index) {
                  <div class="flex items-stretch gap-2 group">
                    <input
                      pInputText
                      [formControl]="ctrl"
                      class="w-full py-1! px-2!"
                    />
                    <button
                      pButton
                      icon="pi pi-trash"
                      severity="danger"
                      size="small"
                      type="button"
                      class="opacity-0 group-hover:opacity-100 transition-opacity"
                      (click)="deleteItem('postLogoutRedirectUris', i)">
                    </button>
                  </div>
                }
                <button
                  pButton
                  label="Add URI"
                  icon="pi pi-plus"
                  outlined
                  size="small"
                  type="button"
                  (click)="addItem('postLogoutRedirectUris')">
                </button>

              </div>
            </div>

            <!-- TOKEN SETTINGS -->
            <div>
              <h3 class="text-base font-semibold text-surface-900 dark:text-surface-0 mb-1">
                Token Settings
              </h3>

              <div class="space-y-4">

                <!-- Access Token TTL -->
                <div formGroupName="accessTokenTTL">
                  <label class="block font-medium mb-1">
                    Access Token Time to Live
                  </label>

                  <div class="flex gap-2 items-center">
                    <input
                      pInputText
                      type="number"
                      formControlName="value"
                      min="1"
                      class="w-24"
                    />

                    <p-select
                      [options]="ttlUnits"
                      formControlName="unit"
                      optionLabel="label"
                      optionValue="value"
                      appendTo="body"
                      class="w-32"
                    ></p-select>
                  </div>
                </div>

                <!-- Refresh Token TTL -->
                <div formGroupName="refreshTokenTTL">
                  <label class="block font-medium mb-1">
                    Refresh Token Time to Live
                  </label>

                  <div class="flex gap-2 items-center">
                    <input
                      pInputText
                      type="number"
                      formControlName="value"
                      min="1"
                      class="w-24"
                    />

                    <p-select
                      [options]="ttlUnits"
                      formControlName="unit"
                      appendTo="body"
                      optionValue="value"
                      class="w-32"
                    ></p-select>
                  </div>
                </div>

                <!-- Checkbox -->
                <div class="flex items-center gap-2">
                  <p-checkbox formControlName="reuseRefreshTokens" binary="true"></p-checkbox>
                  <label>Reuse Refresh Tokens</label>
                </div>

              </div>
            </div>
            <!-- SAVE -->
            <div class="pt-4 border-t border-surface-200 dark:border-zinc-800">
              <p-button
                label="Save Changes"
                icon="pi pi-check"
                type="button"
                [disabled]="!isFormDirty() || isSaving()"
                [loading]="isSaving()"
                (click)="save()"
              ></p-button>
            </div>
          </form>
        }
      </div>
    }
  </app-state-container>
</app-page-layout>